import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Label } from "./ui/label";

export const BookDemoForm = ({ defaultModelId = "" }) => {
  const [models, setModels] = useState([]);
  const [date, setDate] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { model_id: defaultModelId },
  });
  const modelId = watch("model_id");

  useEffect(() => {
    api.get("/models").then(({ data }) => setModels(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (defaultModelId) setValue("model_id", defaultModelId);
  }, [defaultModelId, setValue]);

  const onSubmit = async (form) => {
    setSubmitting(true);
    try {
      const chosen = models.find((m) => m.id === form.model_id);
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        location: form.location,
        model_id: form.model_id || undefined,
        model_name: chosen?.name,
        preferred_date: date ? format(date, "yyyy-MM-dd") : undefined,
        message: form.message || undefined,
      };
      await api.post("/bookings", payload);
      setDone(true);
      toast.success("Demo booked", { description: "Our team will reach out shortly." });
      reset();
      setDate(undefined);
    } catch (e) {
      toast.error("Booking failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto text-center bg-white border border-black/5 rounded-3xl p-12" data-testid="booking-success">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF6A13]/10 text-[#FF6A13]">
          <CheckCircle2 size={30} />
        </div>
        <h3 className="h2-section text-3xl mt-6">Thanks. We're on it.</h3>
        <p className="text-[#86868B] mt-3">
          A Bhumi Equipments specialist will contact you within 1 business day
          to schedule your demo.
        </p>
        <Button
          className="mt-6 rounded-full bg-[#FF6A13] hover:bg-[#E85A0D] text-white px-6"
          onClick={() => setDone(false)}
          data-testid="book-another-btn"
        >
          Book another demo
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-4xl mx-auto grid md:grid-cols-2 gap-5 bg-white p-8 md:p-10 rounded-3xl border border-black/5"
      data-testid="book-demo-form"
    >
      <div className="md:col-span-1">
        <Label htmlFor="name" className="text-xs uppercase tracking-widest text-[#86868B]">Full Name*</Label>
        <Input
          id="name"
          data-testid="form-name"
          className="mt-2 h-12 rounded-xl border-black/10 focus-visible:ring-[#FF6A13]"
          {...register("name", { required: true, minLength: 2 })}
        />
        {errors.name && <p className="text-xs text-red-500 mt-1">Please enter your name</p>}
      </div>

      <div className="md:col-span-1">
        <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-[#86868B]">Phone*</Label>
        <Input
          id="phone"
          data-testid="form-phone"
          type="tel"
          placeholder="+91"
          className="mt-2 h-12 rounded-xl border-black/10 focus-visible:ring-[#FF6A13]"
          {...register("phone", { required: true, minLength: 6 })}
        />
        {errors.phone && <p className="text-xs text-red-500 mt-1">Please enter a valid phone</p>}
      </div>

      <div>
        <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#86868B]">Email</Label>
        <Input
          id="email"
          data-testid="form-email"
          type="email"
          className="mt-2 h-12 rounded-xl border-black/10 focus-visible:ring-[#FF6A13]"
          {...register("email")}
        />
      </div>

      <div>
        <Label htmlFor="location" className="text-xs uppercase tracking-widest text-[#86868B]">Location / City*</Label>
        <Input
          id="location"
          data-testid="form-location"
          className="mt-2 h-12 rounded-xl border-black/10 focus-visible:ring-[#FF6A13]"
          placeholder="Panvel, Pune, Mumbai…"
          {...register("location", { required: true })}
        />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-[#86868B]">Model of Interest</Label>
        <Select value={modelId} onValueChange={(v) => setValue("model_id", v)}>
          <SelectTrigger
            data-testid="form-model"
            className="mt-2 h-12 rounded-xl border-black/10 focus:ring-[#FF6A13]"
          >
            <SelectValue placeholder="Select a Bobcat model" />
          </SelectTrigger>
          <SelectContent>
            {models.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-[#86868B]">Preferred Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="mt-2 h-12 w-full justify-start rounded-xl border-black/10 font-normal text-left"
              data-testid="form-date-trigger"
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-[#FF6A13]" />
              {date ? format(date, "PPP") : <span className="text-[#86868B]">Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={{ before: new Date() }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="md:col-span-2">
        <Label htmlFor="message" className="text-xs uppercase tracking-widest text-[#86868B]">Tell us about your site / needs</Label>
        <Textarea
          id="message"
          data-testid="form-message"
          rows={4}
          className="mt-2 rounded-xl border-black/10 focus-visible:ring-[#FF6A13]"
          {...register("message")}
        />
      </div>

      <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-xs text-[#86868B]">
          By submitting, you agree we may contact you about your booking.
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-[#FF6A13] hover:bg-[#E85A0D] text-white h-12 px-8 font-semibold"
          data-testid="book-demo-submit-button"
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitting ? "Submitting…" : "Confirm Demo Booking"}
        </Button>
      </div>
    </form>
  );
};
